import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createGlobalJobPreset, createGlobalJobPresetVersion, updateJobPreset } from 'api/jobPreset';
import { SITE_HEADER_NUMBER } from 'components/SiteHeader/SiteHeader';
import { getTranslucentSurfaceStyles } from 'components/theme/theme';
import { OrchestrationDataType } from 'interfaces/calculateFeatures';
import { AttributesTable } from 'interfaces/inferenceArtifacts';
import { JobPreset, JobPresetAdmin } from 'interfaces/jobPreset';
import PostProcessingAction, {
  ActionInput,
  InputSource,
  InputType,
  MappingFilterMetadata,
  PostProcessingActionCreated,
} from 'interfaces/postProcessingAction';
import { cloneDeep, Dictionary, filter, flatMap, get, isArray, isEmpty, map, set, size } from 'lodash';
import { useSnackbar } from 'notistack';
import React, { useState } from 'react';
import { DEPENDENT_ON_INPUT_TYPE } from './consts';
import { FeatureActions } from './FeaturesStep';
import { PostProcessingActions } from './PostProcessingActions';
import { getRelevantSelectedStainsForInput } from './PostProcessingActions/PostProcessingAction';
import usePostProcessingActions, { useActionsQuery } from './PostProcessingActions/usePostProcessingActions';
import { getIsRequired, validateInput } from './PostProcessingActions/utils';
import {
  extractNewCellsFromPreset,
  PresetActionAccess,
  PresetEncapsulation,
  PresetJsonData,
} from './Presets/presetUtils';
import {
  convertFeatureActionsFromBackend,
  convertFeatureActionsToBackend,
  convertPostProcessingActionsFromBackend,
  convertPostProcessingActionsToBackend,
} from './utils';

interface PresetAuthoringModeProps {
  preset?: JobPresetAdmin;
  onClose: () => void;
}

interface PresetAuthoringFormProps extends PresetAuthoringModeProps {
  actionDefinitionsById: Dictionary<PostProcessingAction>;
}

type AuthoringSection = 'postprocessing' | 'features';

interface AuthoringOption {
  id: string | number;
  label: string;
  input?: string;
  groupBy?: string;
}

const presetCatalogSourceByStudySource: Partial<Record<InputSource, InputSource>> = {
  [InputSource.INTERMEDIATE_AREAS_PER_TARGET_STAIN]: InputSource.AREAS,
  [InputSource.CLASSES_CONFIG_AREAS_PER_TARGET_STAIN]: InputSource.AREAS,
  [InputSource.INTERMEDIATE_CELLS_PER_TARGET_STAIN]: InputSource.CELLS,
  [InputSource.CLASSES_CONFIG_CELLS_PER_TARGET_STAIN]: InputSource.CELLS,
  [InputSource.INTERMEDIATE_CELLS_FEATURES_PER_TARGET_STAIN]: InputSource.CELLS,
};

const getPresetCatalogSource = (optionSource: string | string[]) =>
  isArray(optionSource) ? undefined : presetCatalogSourceByStudySource[optionSource as InputSource];

const createEmptyPresetJson = (): PresetJsonData => ({
  postprocessing: [],
  features: [],
  visualizations: [],
  gridBasedCalculationParams: {},
  modelPreferencesByModelId: {},
});

const getInitialAccessByActionId = (presetJson: PresetJsonData): Record<string, PresetActionAccess> => {
  const accessByActionId: Record<string, PresetActionAccess> = {};
  map(presetJson.encapsulation?.actions, (action) => {
    accessByActionId[action.actionInstanceId] = action.access;
  });
  return accessByActionId;
};

const hasPortableValue = (value: unknown) =>
  value !== undefined && value !== null && value !== '' && (!isArray(value) || value.length > 0);

const createPresetCatalogMappingMetadata = (
  actionAttributesTable: AttributesTable,
  areaOptions: AuthoringOption[],
  cellOptions: AuthoringOption[]
): MappingFilterMetadata[] => [
  {
    sourceName: 'area',
    sourceDisplayName: 'Area',
    data: [
      {
        label: 'label',
        displayLabel: 'Label',
        options: areaOptions,
        type: OrchestrationDataType.Categorical,
      },
    ],
  },
  ...(actionAttributesTable === AttributesTable.CellTable
    ? [
        {
          sourceName: 'cell',
          sourceDisplayName: 'Cell',
          data: [
            {
              label: 'label',
              displayLabel: 'Label',
              options: cellOptions,
              type: OrchestrationDataType.Categorical,
            },
          ],
        },
      ]
    : []),
];

type AuthoringOptionsResolver = (
  optionSource: string | string[],
  selectedStains: string[],
  index: number,
  actionInput: string,
  inputSourceDependentOn?: string
) => AuthoringOption[];

const getInvalidActionInputCount = (
  actions: PostProcessingActionCreated[],
  actionDefinitionsById: Dictionary<PostProcessingAction>,
  accessByActionId: Record<string, PresetActionAccess>,
  getOptions: AuthoringOptionsResolver,
  getMappingFiltersMetadata: (
    stain: string,
    actionInput: string,
    actionAttributesTable: AttributesTable,
    index: number
  ) => MappingFilterMetadata[]
) =>
  flatMap(actions, (action, index) => {
    const actionDefinition = actionDefinitionsById[action.actionId];
    if (!actionDefinition) return ['Missing action definition'];

    return filter(actionDefinition.inputs, (input) => {
      const value = get(action, input.inputKey);
      const options =
        input.inputType === InputType.LOGICAL_QUERY
          ? getMappingFiltersMetadata(
              action.stain,
              action.actionInput,
              input.attributesTable ?? AttributesTable.CellTable,
              index
            )
          : getOptions(
              input.inputSource,
              getRelevantSelectedStainsForInput(input, action),
              index,
              action.actionInput,
              get(action, input.inputSourceDependentOn)
            );
      const isDeferredInput =
        !input.freeSolo &&
        (typeof input.inputSource === 'string' || input.inputType === InputType.LOGICAL_QUERY) &&
        isEmpty(options);
      if (isDeferredInput) {
        return accessByActionId[action.id] === 'locked' && getIsRequired(input.required, action)
          ? !hasPortableValue(value)
          : false;
      }
      return !validateInput(value, input, action, options);
    });
  }).length;

const PresetAuthoringForm: React.FC<PresetAuthoringFormProps> = ({ preset, onClose, actionDefinitionsById }) => {
  const theme = useTheme();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const [basePresetJson] = useState(() =>
    cloneDeep((preset?.presetJson as PresetJsonData | undefined) || createEmptyPresetJson())
  );
  const { getOptions, getOptionsByValue, getMappingFiltersMetadataForLogicalQuery, stainTypeOptions } =
    usePostProcessingActions([], {}, {}, {}, undefined, [], [], {});

  const getPresetStaticOptions = (optionSource: string | string[]): AuthoringOption[] => {
    if (isArray(optionSource)) return [];
    if (optionSource === InputSource.STAIN_TYPES_OF_SELECTED_SLIDES) {
      return map(
        filter(stainTypeOptions, (stainType) => !stainType.deletedAt),
        (stainType) => ({
          id: stainType.id,
          label: stainType.displayName || stainType.id,
        })
      );
    }
    if (
      optionSource === InputSource.AREA_ATTRIBUTES_OF_SELECTED_SLIDES ||
      optionSource === InputSource.AREA_ATTRIBUTES_OF_SELECTED_SLIDES_FOR_REASSIGNMENT
    ) {
      return [{ id: 'area.label', label: 'Area Label', input: 'area', groupBy: 'Area' }];
    }
    if (
      optionSource === InputSource.CELL_ATTRIBUTES_OF_SELECTED_SLIDES ||
      optionSource === InputSource.CELL_ATTRIBUTES_OF_SELECTED_SLIDES_FOR_REASSIGNMENT
    ) {
      return [{ id: 'cell.label', label: 'Cell Label', input: 'cell', groupBy: 'Cell' }];
    }
    return [];
  };

  const getAuthoringOptions = (
    optionSource: string | string[],
    selectedStains: string[],
    index: number,
    actions: PostProcessingActionCreated[],
    actionInput: string,
    inputSourceDependentOn?: string
  ): AuthoringOption[] => {
    const options = getOptions(
      optionSource,
      selectedStains,
      index,
      actions,
      actionInput,
      actionDefinitionsById,
      inputSourceDependentOn
    );
    const staticOptions = getPresetStaticOptions(optionSource);
    if (isEmpty(options) && !isEmpty(staticOptions)) return staticOptions;
    const presetCatalogSource = getPresetCatalogSource(optionSource);
    return isEmpty(options) && presetCatalogSource
      ? getOptions(presetCatalogSource, [], index, actions, actionInput, actionDefinitionsById, inputSourceDependentOn)
      : options;
  };
  const getAuthoringOptionsByValue = (
    optionSource: string | string[],
    selectedStains: string[],
    index: number,
    actions: PostProcessingActionCreated[],
    actionInput: string,
    inputSourceDependentOn?: string
  ) => {
    const optionsByValue = getOptionsByValue(
      optionSource,
      selectedStains,
      index,
      actions,
      actionInput,
      actionDefinitionsById,
      inputSourceDependentOn
    );
    const staticOptionsByValue = Object.fromEntries(
      map(getPresetStaticOptions(optionSource), (option) => [option.id, option])
    );
    if (isEmpty(optionsByValue) && !isEmpty(staticOptionsByValue)) return staticOptionsByValue;
    const presetCatalogSource = getPresetCatalogSource(optionSource);
    return isEmpty(optionsByValue) && presetCatalogSource
      ? getOptionsByValue(
          presetCatalogSource,
          [],
          index,
          actions,
          actionInput,
          actionDefinitionsById,
          inputSourceDependentOn
        )
      : optionsByValue;
  };
  const getAuthoringMappingMetadata = (
    stain: string,
    index: number,
    actions: PostProcessingActionCreated[],
    actionInput: string,
    actionAttributesTable: AttributesTable
  ) => {
    const metadata = getMappingFiltersMetadataForLogicalQuery(
      stain,
      index,
      actions,
      actionInput,
      actionDefinitionsById,
      actionAttributesTable
    );
    if (!isEmpty(metadata)) return metadata;
    return createPresetCatalogMappingMetadata(
      actionAttributesTable,
      getAuthoringOptions(InputSource.AREAS, [], index, actions, actionInput),
      getAuthoringOptions(InputSource.CELLS, [], index, actions, actionInput)
    );
  };
  const restoreDependentInputTypes = (actions: PostProcessingActionCreated[]) =>
    map(actions, (action, index) => {
      const restoredAction = cloneDeep(action);
      map(actionDefinitionsById[action.actionId]?.inputs, (input) => {
        if (input.inputType !== DEPENDENT_ON_INPUT_TYPE || !input.inputSourceDependentOn) return;
        const dependencyInput = actionDefinitionsById[action.actionId]?.inputs.find(
          (candidate) => candidate.inputKey === input.inputSourceDependentOn
        );
        if (!dependencyInput) return;
        const dependencyValue = get(action, input.inputSourceDependentOn);
        const selectedOption = getAuthoringOptions(
          dependencyInput.inputSource,
          action.stain ? [action.stain] : [],
          index,
          actions,
          action.actionInput
        ).find((option) => option.id === dependencyValue);
        const inputType = Object.values(InputType).includes(selectedOption?.input as InputType)
          ? (selectedOption.input as InputType)
          : InputType.SELECT;
        set(restoredAction, `dependentOnInputTypes.${input.inputKey}`, inputType);
      });
      return restoredAction;
    });
  const [initialActions] = useState(() => {
    const sourcePostprocessing = basePresetJson.postprocessing || [];
    const postprocessing = restoreDependentInputTypes(
      convertPostProcessingActionsFromBackend(
        actionDefinitionsById,
        sourcePostprocessing,
        (stain, index, actions, actionInput, _definitions, actionAttributesTable) =>
          getAuthoringMappingMetadata(stain, index, actions, actionInput, actionAttributesTable),
        (optionSource, selectedStains, index, actionInput, inputSourceDependentOn) =>
          getAuthoringOptionsByValue(
            optionSource,
            selectedStains,
            index,
            sourcePostprocessing,
            actionInput,
            inputSourceDependentOn
          )
      )
    );
    const getInitialFeatureMappingMetadata = (stain: string): MappingFilterMetadata[] =>
      getAuthoringMappingMetadata(
        stain,
        size(postprocessing),
        postprocessing,
        ActionInput.FEATURE_FAMILY,
        AttributesTable.CellTable
      );
    const featureConversion = convertFeatureActionsFromBackend(
      actionDefinitionsById,
      basePresetJson.features || [],
      getInitialFeatureMappingMetadata,
      extractNewCellsFromPreset(basePresetJson, getInitialFeatureMappingMetadata)
    );
    return {
      postprocessing,
      features: featureConversion.convertedFeatureActions,
      newCells: featureConversion.newCellsCreated,
    };
  });
  const [name, setName] = useState(preset?.name || '');
  const [activeSection, setActiveSection] = useState<AuthoringSection>('postprocessing');
  const [postProcessingActions, setPostProcessingActions] = useState(initialActions.postprocessing);
  const [featureActions, setFeatureActions] = useState(initialActions.features);
  const [newCells, setNewCells] = useState(initialActions.newCells);
  const [accessByActionId, setAccessByActionId] = useState<Record<string, PresetActionAccess>>(
    getInitialAccessByActionId(basePresetJson)
  );

  const getPostProcessingOptions: AuthoringOptionsResolver = (
    optionSource,
    selectedStains,
    index,
    actionInput,
    inputSourceDependentOn
  ) =>
    getAuthoringOptions(
      optionSource,
      selectedStains,
      index,
      postProcessingActions,
      actionInput,
      inputSourceDependentOn
    );
  const getPostProcessingOptionsByValue = (
    optionSource: string | string[],
    selectedStains: string[],
    index: number,
    actionInput: string,
    inputSourceDependentOn?: string
  ) =>
    getAuthoringOptionsByValue(
      optionSource,
      selectedStains,
      index,
      postProcessingActions,
      actionInput,
      inputSourceDependentOn
    );
  const getPostProcessingMappingMetadata = (
    stain: string,
    actionInput: string,
    actionAttributesTable: AttributesTable,
    index: number
  ) => getAuthoringMappingMetadata(stain, index, postProcessingActions, actionInput, actionAttributesTable);
  const getFeatureOptions: AuthoringOptionsResolver = (
    optionSource,
    selectedStains,
    _index,
    actionInput,
    inputSourceDependentOn
  ) =>
    getAuthoringOptions(
      optionSource,
      selectedStains,
      size(postProcessingActions),
      postProcessingActions,
      actionInput,
      inputSourceDependentOn
    );
  const getFeatureOptionsByValue = (
    optionSource: string | string[],
    selectedStains: string[],
    _index: number,
    actionInput: string,
    inputSourceDependentOn?: string
  ) =>
    getAuthoringOptionsByValue(
      optionSource,
      selectedStains,
      size(postProcessingActions),
      postProcessingActions,
      actionInput,
      inputSourceDependentOn
    );
  const getFeatureMappingMetadata = (stain: string, actionInput: string, actionAttributesTable: AttributesTable) =>
    getAuthoringMappingMetadata(
      stain,
      size(postProcessingActions),
      postProcessingActions,
      actionInput,
      actionAttributesTable
    );
  const allActions = [...postProcessingActions, ...featureActions];
  const invalidInputCount =
    getInvalidActionInputCount(
      postProcessingActions,
      actionDefinitionsById,
      accessByActionId,
      getPostProcessingOptions,
      getPostProcessingMappingMetadata
    ) +
    getInvalidActionInputCount(
      featureActions,
      actionDefinitionsById,
      accessByActionId,
      getFeatureOptions,
      getFeatureMappingMetadata
    );
  const isNameMissing = isEmpty(name.trim());
  const isActionMissing = isEmpty(allActions);

  const saveMutation = useMutation({
    mutationFn: async () => {
      const currentActionIds = new Set(map(allActions, 'id'));
      const encapsulation: PresetEncapsulation = {
        actions: [
          ...map(postProcessingActions, (action) => ({
            section: 'postprocessing' as const,
            actionInstanceId: action.id,
            access: accessByActionId[action.id] || ('editable' as const),
          })),
          ...map(featureActions, (action) => ({
            section: 'features' as const,
            actionInstanceId: action.id,
            access: accessByActionId[action.id] || ('editable' as const),
          })),
        ],
      };
      const presetJson: PresetJsonData = {
        ...basePresetJson,
        name: name.trim(),
        postprocessing: convertPostProcessingActionsToBackend(actionDefinitionsById, postProcessingActions),
        features: convertFeatureActionsToBackend(actionDefinitionsById, featureActions, newCells),
        highlightedInputs: filter(basePresetJson.highlightedInputs, (input) =>
          currentActionIds.has(input.actionInstanceId)
        ),
        encapsulation,
      };
      const payload = {
        name: name.trim(),
        stains: [] as string[],
        steps: ['calculate_features'],
        presetJson,
        sourceStudyId: null as null,
      };

      if (!preset) return createGlobalJobPreset(payload);
      if (preset.isPublished) {
        return createGlobalJobPresetVersion({ id: preset.id, ...payload });
      }
      return updateJobPreset({ id: preset.id, ...payload });
    },
    onSuccess: (savedPreset: JobPreset) => {
      queryClient.invalidateQueries({ queryKey: ['jobPresets'] });
      queryClient.invalidateQueries({ queryKey: ['jobPresets', 'admin'] });
      queryClient.invalidateQueries({
        queryKey: ['jobPresetVersions', savedPreset.presetId],
      });
      enqueueSnackbar(preset?.isPublished ? 'Preset version created' : 'Preset saved', {
        variant: 'success',
      });
      onClose();
    },
    onError: () => enqueueSnackbar('Preset could not be saved', { variant: 'error' }),
  });

  const updateAccess = (actionId: string, access: PresetActionAccess) =>
    setAccessByActionId((current) => ({ ...current, [actionId]: access }));

  const canSave = !isNameMissing && !isActionMissing && invalidInputCount === 0 && !saveMutation.isLoading;
  const saveLabel = preset?.isPublished ? 'Create new version' : 'Save preset';
  const blockingMessage = isNameMissing
    ? 'Enter a preset name.'
    : isActionMissing
    ? 'Add at least one post-processing action or feature.'
    : `Complete ${invalidInputCount} required ${
        invalidInputCount === 1 ? 'setting' : 'settings'
      }. Locked actions cannot defer required study values.`;

  const sharedPresetAuthoringProps = {
    presetAccessByActionId: accessByActionId,
    onPresetAccessChange: updateAccess,
  };

  return (
    <Stack spacing={2.5}>
      <Box
        data-testid="preset-authoring-sticky-header"
        sx={{
          position: 'sticky',
          top: SITE_HEADER_NUMBER,
          zIndex: theme.zIndex.appBar - 1,
          mx: { xs: -1, md: -1.5 },
          px: { xs: 1, md: 1.5 },
          py: 1.5,
          ...getTranslucentSurfaceStyles(theme),
          borderBottom: 1,
          borderColor: 'divider',
          borderRadius: 0,
        }}
      >
        <Stack spacing={1.5}>
          <Stack direction="row" alignItems="flex-start" spacing={1.5}>
            <IconButton aria-label="Back to presets" onClick={onClose}>
              <ArrowBackRoundedIcon />
            </IconButton>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography variant="h3">{preset ? `Edit ${preset.name}` : 'Create global preset'}</Typography>
              <Typography variant="body2" color="text.secondary">
                Rename this preset and update its actions here. Save stays in this header so it remains available while
                editing.
              </Typography>
            </Box>
          </Stack>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', md: 'flex-start' }}>
            <TextField
              required
              label="Preset name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              helperText="This name is shown in Global Settings and in the job-launch preset list."
              sx={{ width: { xs: '100%', md: 480 }, flexGrow: { md: 1 }, maxWidth: { md: 560 } }}
            />
            <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'block' } }} />
            <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ pt: { md: 0.5 } }}>
              <Button onClick={onClose}>Cancel</Button>
              <Button
                variant="contained"
                startIcon={<CheckRoundedIcon />}
                disabled={!canSave}
                onClick={() => saveMutation.mutate()}
              >
                {saveLabel}
              </Button>
            </Stack>
          </Stack>
          {(isNameMissing || isActionMissing || invalidInputCount > 0) && (
            <Alert severity="info">{blockingMessage}</Alert>
          )}
        </Stack>
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={activeSection} onChange={(_, section: AuthoringSection) => setActiveSection(section)}>
          <Tab value="postprocessing" label={`Post-processing (${postProcessingActions.length})`} />
          <Tab value="features" label={`Features (${featureActions.length})`} />
        </Tabs>
      </Box>

      {activeSection === 'postprocessing' ? (
        <PostProcessingActions
          actions={postProcessingActions}
          setActions={setPostProcessingActions}
          updateClassConfigAccordingToActions={() => undefined}
          getOptions={getPostProcessingOptions}
          getOptionsByValue={getPostProcessingOptionsByValue}
          getMappingFiltersMetadataForLogicalQuery={getPostProcessingMappingMetadata}
          {...sharedPresetAuthoringProps}
        />
      ) : (
        <FeatureActions
          actions={featureActions}
          setActions={setFeatureActions}
          stains={[]}
          getAdditionalInputError={() => ''}
          newCells={newCells}
          setNewCells={setNewCells}
          allowNewCellFilters={false}
          getOptions={getFeatureOptions}
          getOptionsByValue={getFeatureOptionsByValue}
          getMappingFiltersMetadataForLogicalQuery={getFeatureMappingMetadata}
          {...sharedPresetAuthoringProps}
        />
      )}
    </Stack>
  );
};

export const PresetAuthoringMode: React.FC<PresetAuthoringModeProps> = (props) => {
  const { postProcessingActionsById, isLoadingActions, isActionsError } = useActionsQuery();

  if (isLoadingActions) {
    return (
      <Stack alignItems="center" spacing={1.5} sx={{ py: 6 }}>
        <CircularProgress />
        <Typography color="text.secondary">Loading action definitions…</Typography>
      </Stack>
    );
  }

  if (isActionsError) {
    return <Alert severity="error">Action definitions could not be loaded. Please try again.</Alert>;
  }

  return <PresetAuthoringForm {...props} actionDefinitionsById={postProcessingActionsById} />;
};
